import {
    maxBulkInvitees,
    ReferenceContactId,
    ReferralMappings,
    RegionId,
} from "@sp-crm/core";
import { graphqlFetcher } from "backend/fetcher";
import { EventInviteeDto } from "components/calendar/calendar-types";
import { ReferenceSearchBar } from "components/references/dashboard-reference/reference-search-bar";
import { buildReferenceContactSearchRequest } from "components/references/reference-search-request";
import { SelectedReferenceContact } from "components/references/select-reference/reference-contact-picker";
import { SearchRequestPagination } from "components/simple-search/search-request-pagination";
import { Checkbox } from "components/ui/checkbox";
import { fancyAlert } from "components/ui/fancy-alert";
import { fancyConfirm } from "components/ui/fancy-confirm";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryButton } from "components/ui/secondary-button";
import { Spinner } from "components/ui/spinner";
import {
    ReferenceContactSearchDocument,
    ReferenceContactSearchQuery,
    ReferenceContactSearchQueryVariables,
    useReferenceContactSearchQuery,
} from "generated/graphql";
import React, { useCallback, useMemo, useState } from "react";
import { useRegionId } from "store/selectors/hooks";
import { initialSearchFilter, ReferenceFilter } from "store/slices/reference-dashboard";
import { parsePhone } from "util/phone";
import { pluralize } from "util/pluralize";
import { NO_NAME } from "util/text";

type ContactHit = ReferenceContactSearchQuery["referenceContactSearch"]["hits"][0];

interface PagingValue {
    page: number;
    perPage: number;
}

const initialPaging: PagingValue = { page: 0, perPage: 25 };
const sort = { sort: "numberOfReferrals", sortDirection: "DESC" as const };

const selectedContactFromHit = (hit: ContactHit): SelectedReferenceContact => ({
    referenceContactId: hit.id,
    contactId: null,
    businessId: hit.businessId ?? null,
    communityId: hit.communityId ?? null,
    name: hit.name ?? null,
    businessName: hit.organizationName ?? null,
});

const listedContactIds = (invitees: EventInviteeDto[]): Set<ReferenceContactId> =>
    new Set(
        invitees
            .filter(invitee => invitee.entityType === "contact")
            .map(invitee => invitee.entityId as ReferenceContactId),
    );

interface EventInviteePickerProps {
    invitees: EventInviteeDto[];
    onCommit: (selected: SelectedReferenceContact[]) => Promise<boolean>;
    onCancel: () => void;
}

export const EventInviteePicker: React.FC<EventInviteePickerProps> = props => {
    const { invitees, onCommit, onCancel } = props;
    const regionId = useRegionId();
    const [filter, setFilter] = useState<ReferenceFilter>(initialSearchFilter);
    const [paging, setPaging] = useState<PagingValue>(initialPaging);
    const [selected, setSelected] = useState<
        ReadonlyMap<ReferenceContactId, SelectedReferenceContact>
    >(new Map());
    const [committing, setCommitting] = useState(false);

    const request = useMemo(
        () => ({
            regionId: regionId ?? ("" as RegionId),
            ...buildReferenceContactSearchRequest(filter, sort, paging),
        }),
        [regionId, filter, paging],
    );
    const query = useReferenceContactSearchQuery(
        { search: request },
        { keepPreviousData: true, enabled: !!regionId },
    );
    const results = query.data?.referenceContactSearch ?? {
        hits: [] as ContactHit[],
        total: 0,
    };
    const listed = useMemo(() => listedContactIds(invitees), [invitees]);

    const handleFilterChange = useCallback((next: ReferenceFilter) => {
        setFilter(next);
        setPaging(current => ({ ...current, page: 0 }));
    }, []);

    const handleSelect = useCallback((hit: ContactHit) => {
        setSelected(previous =>
            new Map(previous).set(hit.id, selectedContactFromHit(hit)),
        );
    }, []);

    const handleDeselect = useCallback((hit: ContactHit) => {
        setSelected(previous => {
            const next = new Map(previous);
            next.delete(hit.id);
            return next;
        });
    }, []);

    const handleAddSelected = useCallback(async () => {
        setCommitting(true);
        try {
            const added = await onCommit(Array.from(selected.values()));
            if (added) {
                setSelected(new Map());
            }
        } finally {
            setCommitting(false);
        }
    }, [onCommit, selected]);

    const handleAddAll = useCallback(async () => {
        setCommitting(true);
        try {
            const total = results.total;
            if (total > maxBulkInvitees) {
                await fancyAlert(
                    "Too many matches",
                    `${total} contacts match. Adding everyone at once works with up to ${maxBulkInvitees} matches. Narrow the search or filters first, or check guests individually.`,
                    "warning",
                );
                return;
            }
            if (total === 0) {
                await onCommit([]);
                return;
            }
            const confirmed = await fancyConfirm(
                "Add all matching",
                `Add ${total} ${pluralize(total, "guest", "guests")} to the guest list?`,
                "Add them",
                "Cancel",
            );
            if (!confirmed) {
                return;
            }
            const data = await graphqlFetcher<
                ReferenceContactSearchQuery,
                ReferenceContactSearchQueryVariables
            >(ReferenceContactSearchDocument, {
                search: { ...request, page: 0, perPage: maxBulkInvitees },
            })();
            await onCommit(data.referenceContactSearch.hits.map(selectedContactFromHit));
        } catch {
            await fancyAlert(
                "Could not add guests",
                "Something went wrong finding those guests. Please try again.",
                "warning",
            );
        } finally {
            setCommitting(false);
        }
    }, [results.total, request, onCommit]);

    return (
        <div>
            <div className="space-y-3 p-4">
                <ReferenceSearchBar
                    value={filter}
                    onChange={handleFilterChange}
                    layout="stacked"
                />
                <div className="flex flex-wrap items-center justify-between gap-2">
                    {query.data ? (
                        <p className="text-sm text-gray-600">
                            {results.total}{" "}
                            {pluralize(results.total, "contact", "contacts")} found. Check
                            contacts to select them, then add them below.
                        </p>
                    ) : (
                        <p className="text-sm text-gray-600">Searching…</p>
                    )}
                    <SecondaryButton
                        disabled={committing || results.total === 0}
                        onClick={handleAddAll}>
                        Add all matching
                    </SecondaryButton>
                </div>
                {query.isLoading ? <Spinner /> : null}
                <ul className="space-y-2">
                    {results.hits.map(hit => (
                        <li key={hit.id}>
                            <PickerContactRow
                                hit={hit}
                                alreadyListed={listed.has(hit.id)}
                                checked={listed.has(hit.id) || selected.has(hit.id)}
                                disabled={listed.has(hit.id) || committing}
                                onSelect={handleSelect}
                                onDeselect={handleDeselect}
                            />
                        </li>
                    ))}
                </ul>
                {query.data ? (
                    <SearchRequestPagination
                        request={{ ...paging, ...sort }}
                        total={results.total}
                        onPageChange={page =>
                            setPaging(current => ({ ...current, page }))
                        }
                        onPageSizeChange={perPage => setPaging({ page: 0, perPage })}
                        allowAll={false}
                    />
                ) : null}
            </div>
            <div className="sticky bottom-0 flex items-center justify-end gap-2 border-t border-gray-200 bg-white px-4 py-3">
                {committing ? <Spinner /> : null}
                <SecondaryButton disabled={committing} onClick={onCancel}>
                    Cancel
                </SecondaryButton>
                <PrimaryButton
                    disabled={committing || selected.size === 0}
                    onClick={handleAddSelected}>
                    {selected.size === 0
                        ? "Add guests"
                        : `Add ${selected.size} ${pluralize(
                              selected.size,
                              "guest",
                              "guests",
                          )}`}
                </PrimaryButton>
            </div>
        </div>
    );
};

interface PickerContactRowProps {
    hit: ContactHit;
    alreadyListed: boolean;
    checked: boolean;
    disabled: boolean;
    onSelect: (hit: ContactHit) => void;
    onDeselect: (hit: ContactHit) => void;
}

const PickerContactRow: React.FC<PickerContactRowProps> = props => {
    const { hit, alreadyListed, checked, disabled, onSelect, onDeselect } = props;
    const orgTypeMapping =
        hit.organizationType && ReferralMappings[hit.organizationType]
            ? ReferralMappings[hit.organizationType]
            : null;
    const phone = parsePhone(hit.phone ?? hit.cellPhone ?? "");
    const detailLine = [hit.organizationName, orgTypeMapping?.humanReadable]
        .filter(Boolean)
        .join(" · ");
    const contactLine = [hit.email, phone.digits !== "" ? phone.display : null]
        .filter(Boolean)
        .join(" · ");

    return (
        <div className="flex items-center gap-3 rounded-md border border-gray-200 bg-white px-4 py-3 shadow-sm">
            <Checkbox
                labelClassName="min-w-0 flex-1"
                checked={checked}
                disabled={disabled}
                onChange={e => (e.target.checked ? onSelect(hit) : onDeselect(hit))}
                label={
                    <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-baseline gap-x-2">
                            <span className="font-medium text-gray-900">
                                {hit.name || NO_NAME}
                            </span>
                            {hit.role ? (
                                <span className="text-xs text-gray-500">{hit.role}</span>
                            ) : null}
                        </div>
                        {detailLine ? (
                            <div className="truncate text-sm text-gray-600">
                                {detailLine}
                            </div>
                        ) : null}
                        {contactLine ? (
                            <div className="truncate text-sm text-gray-500">
                                {contactLine}
                            </div>
                        ) : null}
                    </div>
                }
            />
            {alreadyListed ? (
                <span className="shrink-0 text-xs text-gray-500">On guest list</span>
            ) : null}
        </div>
    );
};
